import React from 'react';
import { Alert } from '@smarterservices/smarter-ui';
import { BlockRemoteRenderer, BlockKitProvider } from '@shared/components/BlockKit';
import Loader from '@ui/context/session/components/Common/Loader';
import { useExamSessionStore } from '../../../stores/StoreContext';

/**
 * CustomTab Component
 * 
 * Renders a custom tab by fetching and displaying BlockKit content from a remote URL.
 * Uses BlockRemoteRenderer to handle fetching, loading states, and rendering.
 * Automatically appends session data to the URL as query parameters.
 * 
 * Query parameters added:
 * - sessionSid: Current session identifier
 * - installSid: Installation identifier
 * - examSid: Exam identifier
 * - tabId: Custom tab identifier
 * 
 * @param {Object} props
 * @param {Object} props.tab - Tab configuration object
 * @param {string} props.tab.id - Tab identifier
 * @param {string} props.tab.title - Tab display title
 * @param {string} props.tab.url - URL to fetch BlockKit JSON from
 * @param {Object} [props.replacements] - Optional replacements for BlockKit context
 */
function CustomTab({ tab, replacements = {} }) {
    // Get session data from store
    const sessionSid = useExamSessionStore((state) => state.sessionSid);
    const installSid = useExamSessionStore((state) => state.installSid);
    const examSid = useExamSessionStore((state) => state.examSid);

    // Handle loading with custom loading display
    const handleLoading = () => {
        return <Loader spinnerSize="medium" text={`Loading ${tab?.title || 'content'}...`} />;
    };

    // Handle empty with custom empty display
    const handleEmpty = () => {
        return (
            <div style={{ padding: '1rem' }}>
                <Alert variant="info" margin="small" hasShadow={false}>
                    No content available for {tab?.title || 'this tab'}.
                </Alert>
            </div>
        );
    };

    // Handle error with custom error display
    const handleError = (error) => {
        return (
            <div style={{ padding: '1rem' }}>
                <Alert variant="error" margin="small" hasShadow={false}>
                    <div>
                        <strong>Failed to load {tab?.title || 'custom tab'}</strong>
                        <div style={{ marginTop: '0.5rem' }}>{error.message}</div>
                        {tab?.url && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.8 }}>
                                URL: {tab.url}
                            </div>
                        )}
                    </div>
                </Alert>
            </div>
        );
    };

    // Render using BlockRemoteRenderer wrapped in BlockKitProvider
    return (
        <BlockKitProvider replacements={replacements}>
            <BlockRemoteRenderer
                url={tab?.url}
                contentId={tab?.id}
                data={{
                    sessionSid,
                    installSid,
                    examSid,
                    tabId: tab?.id
                }}
                onLoading={handleLoading}
                onEmpty={handleEmpty}
                onError={handleError}
            />
        </BlockKitProvider>
    );
}

export default CustomTab;
